import React from 'react';

/**
 * options: {
 *  scrollWindow     (bool)               => Hace scroll sobre el nodo Window directamente, en caso contrario hará scroll sobre el elemento parent del componente con el HOC de scroll.
 *  scrollDistance   (obj) {x: px, y: px} => Distancia que recorrerá el scroll por cada eje. Por defecto es 0 en ambos y solo recorrerá la distancia suficiente para que el elemento entre en el viewport. 
 *  scrollSpeed      (number)             => Velocidad del scroll, por defecto 10. (Cantidad de pixeles que se desplazará por iteracion hasta llegar a la scrollDistance, cuanto mayor sea más rapido lo realizará)
 * }
 */
export default (options) => (WrappedComponent) => {

    return class extends React.Component {

        constructor(props) {
            super(props);
            
            this.refElement      = React.createRef();
            this.scrollTimers    = {};

            this.scrollWindow    = null;
            this.scrollDistance  = null;
            this.scrollSpeed     = null;
            this.scrollAxis      = null;

        }

        optionsInitializer = () => {

            this.scrollDistance  = options.scrollDistance  || { x: 0, y: 0};
            this.scrollWindow    = options.scrollWindow !== undefined ? options.scrollWindow : true;
            this.scrollSpeed     = options.scrollSpeed     || 10;

            let { current: domElement }  = this.refElement;
            const overflowBounds = this.isOutOfViewport(domElement);

            // Comprobamos si el elemento referenciado sobresale del viewport por algún lado y seteamos las axis que se deberán scrollear.
            if(overflowBounds.bothXY) {
                this.scrollAxis = { y: true, x: true };
            } else if(overflowBounds.top || overflowBounds.bottom) {
                this.scrollAxis = { y: true, x: false };
            } else if(overflowBounds.right || overflowBounds.left) {
                this.scrollAxis = { y: false, x: true };
            } else {
                this.scrollAxis = { y: false, x: false };
            }
            
            // Metodo que setea la distancia de scroll de cada axis.
            this.setScrollDistance(overflowBounds);
        }

        componentDidMount = () => {
            document.addEventListener('mousedown', this.outsideClickHandler);
            
            // Al renderizarse el componente inicializamos las opciones segun el parametro <options> recibido.
            this.optionsInitializer();

            this.smoothScrollHandler();
        }

        componentWillUnmount = () => {
            document.removeEventListener('mousedown', this.outsideClickHandler);
        }

        outsideClickHandler = e => {
            const { current: domElement }  = this.refElement;
            if(domElement !== e.target) {
                axisReference.forEach((axis) => {
                    if(this.scrollAxis[axis]){
                        window.clearTimeout(this.scrollTimers[axis]);
                    }
                }); 
            }
        }

        smoothScrollHandler = () => {
            const scrollElement = this.scrollWindow ? window : this.refElement.current.parentNode;
            axisReference.forEach((axis) => {
                this.smoothScroll(axis, scrollElement, this.scrollAxis, this.scrollDistance, this.scrollSpeed);
            });
        }

        smoothScroll = (axis, scrollElement, scrollAxis, scrollDistance, scrollSpeed, totalScrolled) => {
            let totalScroll = totalScrolled || {x: 0, y: 0};
            let axisScrollSpeed = { y: scrollSpeed, x: scrollSpeed };
            if(scrollAxis[axis]){
                
                let distanceIsAbsolute = valueIsAbsolute(scrollDistance[axis]);
                let scrollDidNotFinish = distanceIsAbsolute ? totalScroll[axis] < scrollDistance[axis] : totalScroll[axis] > scrollDistance[axis];
                axisScrollSpeed[axis] = distanceIsAbsolute ? axisScrollSpeed[axis] : castToNegative(-axisScrollSpeed[axis]);
                
                if(scrollDidNotFinish) {
                    this.scrollTimers[axis] = setTimeout(() => {
                        if(axis === "y"){
                            scrollElement.scrollBy(0, axisScrollSpeed.y);
                        } else {
                            scrollElement.scrollBy(axisScrollSpeed.x, 0);
                        }                 
                        totalScroll[axis] += axisScrollSpeed[axis];
                        this.smoothScroll(axis, scrollElement, scrollAxis, scrollDistance, scrollSpeed, totalScroll);
                    }, 10);
                }
            }
        }

        setScrollDistance = (overflowBounds) => {
            
            // Seteamos la distancia por defecto si no nos han pasado ninguna por el parametro <options>
            if(this.scrollDistance.y && !this.scrollDistance.x) {
                this.scrollDistance.x = 0;
            } else if(this.scrollDistance.x && !this.scrollDistance.y) {
                this.scrollDistance.y = 0;
            }
            
            // En el caso de que se deba scrollear el eje VERTICAL y la distancia sea la default obtenemos la distancia de scroll
            if(this.scrollAxis.y && this.scrollDistance.y === 0) {
                if(overflowBounds.top){
                    this.scrollDistance.y = overflowBounds.top;
                } else if (overflowBounds.bottom) {
                    this.scrollDistance.y = overflowBounds.bottom;
                }
            }

            // En el caso de que se deba scrollear el eje HORIZONTAL y la distancia sea la default obtenemos la distancia de scroll
            if(this.scrollAxis.x && this.scrollDistance.x === 0) {
                if(overflowBounds.left){
                    this.scrollDistance.x = overflowBounds.left;
                } else if (overflowBounds.right) {
                    this.scrollDistance.x = overflowBounds.right;
                }
            }
        }

        isOutOfViewport = (element) => {
            const bounding = element.getBoundingClientRect();
            const out      = {};
            
            const viewport = {};

            if(this.scrollWindow) {
                viewport.height = window.innerHeight || document.documentElement.clientHeight;
                viewport.width  = window.innerWidth  || document.documentElement.clientWidth;
            } else {
                const { parentNode } = element;
                viewport.height = parentNode.offsetHeight;
                viewport.width  = parentNode.offsetWidth;
            }

            out.top     = bounding.top    < 0 ? bounding.top  : false;
            out.left    = bounding.left   < 0 ? bounding.left : false;
            out.bottom  = bounding.bottom > viewport.height ? bounding.bottom - viewport.height : false;
            out.right   = bounding.right  > viewport.width  ? bounding.right  - viewport.width  : false;
            
            out.any     = Number.isInteger(out.top) || Number.isInteger(out.bottom) || Number.isInteger(out.left) || Number.isInteger(out.right);
            out.all     = Number.isInteger(out.top) && Number.isInteger(out.bottom) && Number.isInteger(out.left) && Number.isInteger(out.right);
            out.bothXY  = (Number.isInteger(out.top) || Number.isInteger(out.bottom)) && (Number.isInteger(out.right) || Number.isInteger(out.left))

            return out;
        };

        render() {
            return (
                <div ref={this.refElement}>
                    <WrappedComponent {...this.props} />
                </div>
            );
        }
    }
}

// Referencia de las axis para luego iterarlas (helper).
const axisReference = ["x", "y"];

// Helper muy sencillo para saber si un valor es absoluto.
const valueIsAbsolute = (val) => {
    return val >= 0;
};

// Metodo helper para castear a negativo un numero solo si no lo es ya.
const castToNegative = (value) => {
    return !valueIsAbsolute(value) ? value : -value;
}

